import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { ChangeDocument } from "../../models/machineDocumentsModel";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ThemeProvider,
  createTheme
} from "@mui/material";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { createDocument, getDocuments } from "../../customerMachineDocumentsSlice";
import UploadDocument from "./UploadDocument";
import useTranslation from "../../../../shared/hooks/useTranslation";

export interface CreateDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  machineId: string;
}

const DocumentDialog = ({ open, onClose, machineId }: CreateDocumentDialogProps) => {
  const dispatch = useAppDispatch();
  const { translate: tCommon } = useTranslation("common");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isDirty }
  } = useForm<{ documents: ChangeDocument[] }>();

  const onSubmit: SubmitHandler<{ documents: ChangeDocument[] }> = (data: { documents: ChangeDocument[] }) => {
    handleCreateDocument(data.documents);
  };

  const { fields, append, remove } = useFieldArray<{ documents: ChangeDocument[] }>({
    control,
    name: "documents"
  });

  const handleCreateDocument = async (model: ChangeDocument[]) => {
    model?.forEach(async (file) => {
      if (file.file?.content) {
        await dispatch(createDocument({ machineId, model: file }));
        await dispatch(getDocuments(machineId));
      }
    });
    onClose();
  };

  useEffect(() => {
    const createDocument: { documents: ChangeDocument[] } = {
      documents: [
        {
          name: "",
          file: null
        }
      ]
    };
    reset(createDocument);
  }, []);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px"
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              minWidth: "480px",
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <p className="heading-2">{tCommon("uploadFile.upload")}</p>
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "#333333"
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <form id="crateDocument" onSubmit={handleSubmit(onSubmit)}>
            <UploadDocument
              register={register}
              errors={errors}
              fields={fields}
              append={append}
              remove={remove}
              setValue={setValue}
              clearErrors={clearErrors}
            />
          </form>
        </DialogContent>

        <DialogActions className="flex">
          <Button variant="outlined" className="w-full" onClick={onClose}>
            {tCommon("buttons.cancel")}
          </Button>

          <Button form="crateDocument" type="submit" variant="contained" className="w-full" disabled={!isDirty}>
            {tCommon("uploadFile.uploadFiles")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DocumentDialog;